import { useState, useEffect, useContext } from 'react';
import { useSessionQuery, useSessionMutation, mutationErrorMsg } from '../MsalAccessProvider';

import { useParams, Link } from 'react-router-dom';

import { UserContext } from '../UserProvider';

import SubNavbar from "../SubNavbar";
import { Panel, PanelActionButton, PanelEdit, PanelPrefs } from "../Panel";

import loading from '../../loading.gif';

/////////////////////////
// wizard step components
/////////////////////////

const StepOne = ({user, data}) => {
  return (
    <Panel title="Discord User"
      description={
      <>
        <div>
          You are registering this discord user as <span className="text-white">{user.email}</span> for
          access to the UTRGV CS server.
        </div>
        <ul className="">
          <li className="ml-4 mt-2"><span className="text-white">Not your email address?</span> Sign out and sign back in with the correct account.</li>
          <li className="ml-4 mt-2"><span className="text-white">Not your discord user?</span> Go back to discord, login with the correct user, and get a new registration link.</li>
        </ul>
      </>}>
      <PanelActionButton>{data.request_discord_name}</PanelActionButton>
    </Panel>
  )};

const StepTwo = ({displayName, displayNameChange, mutationCheckName, reset}) => {
  return (
    <Panel title="Discord Display Name"
      description={
      <div className="space-y-4">
        <p>The CS discord is <span className="italic">not anonymous</span>! Your instructors
        know who you are, and we expect you to behave online the same way you do in our
        classrooms.</p>
        <p>We strongly recommend using the name you go by in-person, as you will be
        interacting and networking with your peers in and out of class. However, if you
        would prefer to appear with a different (reasonable, non-offensive) name,
        you can enter it here. Letters and spaces only.</p>
      </div>}
      loadingText={mutationCheckName && mutationCheckName.isLoading && ('Updating display name...')}
      errorText={mutationErrorMsg(mutationCheckName, 'Failed to update display name')}
      >
      <PanelEdit onChange={displayNameChange}
                 value={displayName.name}
                 errorMsg={displayName.error}
                 buttonLabel="Reset" onClick={reset}
      />
    </Panel>
  )};

const StepThree = ({notifications, updateNotification}) => {
  return (
    <Panel title="Discord Notifications"
      description={
      <div>
        Disable to opt-out of notifications for the CS discord announcement channels.
        Only faculty and staff can make announcements.
      </div>}>
      <PanelPrefs prefs={notifications && notifications.map(notification => {
              return {label: notification.name, description: notification.topic,
                      checked: notification.subscribed};
            })}
            onChange={updateNotification}
            />
    </Panel>
  )};

const Register = () => {
  const { user } = useContext(UserContext);
  const { requestToken } = useParams();
  const { data, error, isError, isLoading } = useSessionQuery(`/register/${requestToken}`);
  const mutationRegister = useSessionMutation('/register');
  const mutationCheckName = useSessionMutation('/profile/checkDisplayName');

  // wizard step management
  const [step, setStep] = useState(0);
  const advance = async () => {
    if (step === 0 || step === 1) {
      displayNameValidate(() => setStep(step => step + 1));
    };
  };
  const back = async () => {
    if (step === 1 || step === 2) {
      displayNameValidate(() => setStep(step => step - 1));
    };
  };

  // registration info
  const [displayName, setDisplayName] = useState({name: null, error: null});
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    console.log("effect");
    console.log(data);
    console.log(user);

    if (data) {
      console.log(`setting display name to ${data.current_display_name} || ${user.first_name + ' ' + user.last_name}`);
      setDisplayName({
        name: data.current_display_name || user.first_name + ' ' + user.last_name,
        error: null
      });

      if (data.notifications) {
        if (!data.has_registration) {
          // default notifications all to true
          setNotifications(data.notifications.map(notification => ({...notification, subscribed: true})));
        } else {
          setNotifications(data.notifications);
        }
      }
    }
  }, [data, user]);

  // wait for initial pull
  if (isLoading) {
    return (
      <div className="flex flex-col my-8">
        <div className="flex justify-center pt-4 pb-2">
          <img alt="Loading..." src={loading} className='w-12' />
        </div>
      </div>
    );
  }
  
  if (isError) {
    return (
      <div className="flex flex-col my-8">
        <div className="text-center pt-4 pb-2">
          {error}
        </div>
        <div className="text-center pt-4 pb-2">
          Please contact us at csci@utrgv.edu from your UTRGV email.
        </div>
      </div>
    );
  }

  /////////////////////////
  // display name change actions
  /////////////////////////

  // local validation on each change
  const displayNameChange = async (e) => {
    const name = e.target.value;
    // 32 chars, only letters and spaces
    if (name.trim().length > 32) {
      setDisplayName({name: name, error: "Display name can only be 32 characters long."});
    } else if (name.replace(/[a-z,A-Z]/g, '').trim().length !== 0) {
      setDisplayName({name: name, error: "Display name can only have letters and spaces - no numbers or special characters."});
    } else {
      setDisplayName({name: name, error: ""});
    }
  };

  // display name change
  // blur has a race condition with click, so we have to check this from the next/back
  //  click events
  const displayNameValidate = async (verificationCallback) => {
    // already under pre-stroke error, don't bother
    if (displayName.error) return;

    const name = displayName.name.trim();
    if (!name) {
      // blank, don't bother the server
      setDisplayName({name: name, error: "Display name cannot be blank."});
      return;
    }

    // actual name or already saved name are fine
    const actualName = user.first_name + ' ' + user.last_name;
    if (name === actualName || (data && (name === data.current_display_name))) {
      // (set in case trim changed it)
      setDisplayName({name: name, error:null});
      if (verificationCallback) verificationCallback();
      return;
    }  

    mutationCheckName.mutateAsync({displayName: name}, {
      onSuccess: (response, payload, context) => {
        if (!response.data.success) {
          setDisplayName({name: name, error: "Display name already in use, please choose another."});
        } else {
          setDisplayName({name: name, error: null});
          if (verificationCallback) verificationCallback();
        }  
      }
    });
  };

  const resetDisplayName = () => {
    setDisplayName({
      name: (data && data.current_display_name) || (user.first_name + ' ' + user.last_name),
      error: null
    });
  };

  /////////////////////////
  // notification change actions
  /////////////////////////

  const updateNotification = async (changeIndex, value) => {
    setNotifications(notifications => notifications.map((notification, i) => 
        i === changeIndex ? {...notification, subscribed: value} : notification));
  };
  
  /////////////////////////
  // registration action
  /////////////////////////

  const register = async () => {
    mutationRegister.mutateAsync({
      notifications : notifications.map(notification => 
        ({
          roleId: notification.id,
          subscribed: notification.subscribed
        })),
      displayName: displayName.name,
      requestToken: requestToken
    }, {
      onSuccess: (response, payload, context) => {
        if (response.data.success) {
          setStep(ct => ct + 1);
        } else {
          // indicate unrecoverable error
          setStep(-1);
        }
      }
    });
  }

  /////////////////////////
  // render outcomes and errors
  /////////////////////////

  // complete
  if (step === 3) {
    return (
      <>
        <div className="flex flex-col m-8 space-y-4">
          <div className="text-center">
            Registration complete! Manage your discord account on your <Link className="text-utrgv-blue font-semibold" to="/ptl/profile">profile page</Link>.
          </div>
        </div>
      </>
    );
  }

  // unrecoverable error
  if (step === -1) {
    return (
      <>
        <div className="flex flex-col m-8 space-y-4">
          <div className="text-center">
            Failed to complete registration. For help, please direct message a server ADMIN or MODERATOR, or email us at <span className='font-semibold'>csci@utrgv.edu</span> from your UTRGV email.
          </div>
        </div>
      </>
    );
  }

  // data problems
  if (data.error) {
    return (
      <>
        <div className="flex flex-col m-8 space-y-4">
          <div className="text-center">
            {data.error === 'invalid-request' && `Invalid registration request link.`}
            {data.error === 'already-registered' &&
              <div>
                You are already registered as <span className='font-semibold'>{data.current_discord_name}</span>.
                If you were trying to register a different discord user, get a registration link from the @control
                bot as that user and try again.
              </div>}
            {data.error === 'duplicate-discord-id' && 
              <div>
                The discord user <span className='font-semibold'>{data.current_discord_name}</span> is already registered to another UTRGV account.</div>}
            {data.error === 'already-registered-other' &&
              <div>
                You are already registered as <span className='font-semibold'>{data.current_discord_name}</span>.
                To change discord accounts, unregister first on your <Link className="text-utrgv-blue font-semibold" to="/profile">profile page</Link>, then try again.
              </div>}
          </div>
          <div className="text-center">
            For help, please direct message a server ADMIN or MODERATOR, or email us at <span className='font-semibold'>csci@utrgv.edu</span> from your UTRGV email.
          </div>
        </div>
      </>
    );
  }

  /////////////////////////
  // render steps
  /////////////////////////

  return (
    <div className="flex flex-col mt-6 mx-8 space-y-6 md:tracking-wide">
      {step === 0 ?
        <StepOne user={user} data={data} />
        : step === 1 ?
        <StepTwo displayName={displayName}
                  displayNameChange={displayNameChange}
                  mutationCheckName={mutationCheckName}
                  reset={resetDisplayName}
                  />
        : <StepThree notifications={notifications}
                      updateNotification={updateNotification}/>}
      <div className="flex flex-row justify-center space-x-2">
        {step > 0 &&
          <button 
            className={`rounded px-8 py-2 border 
            ${displayName.error ? 'border-utrgv-lightgray text-utrgv-lightgray' : 'hover:bg-utrgv-blue'}`}
            disabled={displayName.error}
            onClick={back}>
            Back
          </button>}
        {step < 2 ?
          <button 
            className={`rounded px-8 py-2 border 
            ${displayName.error ? 'border-utrgv-lightgray text-utrgv-lightgray' : 'hover:bg-utrgv-blue'}`}
            disabled={displayName.error}
            onClick={advance}>
            Next
          </button>
          :
          <button 
          className={`rounded px-8 py-2 border 
          ${displayName.error ? 'border-utrgv-lightgray text-utrgv-lightgray' : 'hover:bg-utrgv-blue'}`}
          onClick={register}>
            Register
          </button>}
      </div>
    </div>
  );
};

export default Register;
